
import React from "react";

const robotImg = "/lovable-uploads/7d6f778f-ae83-4e61-bca4-03532e8b5b96.png";

type EyeDapticaMessageProps = {
  mode: "full" | "avatar";
  onClick?: () => void;
};

const EyeDapticaMessage: React.FC<EyeDapticaMessageProps> = ({ mode, onClick }) => {
  const robotEl = (
    <div
      className={`relative flex-shrink-0 transition-transform duration-300
        ${mode === "avatar"
          ? "hover:rotate-6 hover:scale-105 cursor-pointer pulse"
          : "hover:scale-105"}
        `}
      style={{
        width: mode === "avatar" ? 80 : 320,
        height: mode === "avatar" ? 80 : 540,
        transition: "transform 300ms cubic-bezier(.16,1,.3,1)",
      }}
      onClick={onClick}
      tabIndex={-1}
      aria-hidden="true"
    >
      <img
        src={robotImg}
        alt="Eye-Daptica Robot"
        className={`w-full h-full object-contain rounded-xl shadow-2xl 
          ${mode === "avatar" 
            ? "border-2 border-[#36314c]/30 bg-transparent" 
            : "border-2 border-[#36314c]/70 bg-[#23242A]"} 
          pointer-events-none select-none`}
        draggable="false"
        style={{
          userSelect: "none",
          pointerEvents: "none",
        }}
      />
    </div>
  );

  if (mode === "avatar") {
    return (
      <div
        className="fixed top-5 right-5 z-[2000] rounded-full border-2 border-[#1a1f2c30] shadow-xl flex flex-col items-center justify-center cursor-pointer bg-transparent transition-all"
        style={{ width: 100, height: 100 }}
        title="EYE-DAPTICA is watching!"
        tabIndex={0}
        aria-label="Eye-Daptica Robot Floating Avatar"
      >
        {robotEl}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-[2500] flex items-center justify-center transition-all duration-700">
      <div className="flex flex-col md:flex-row md:items-center gap-7 md:gap-12 max-w-4xl w-full px-6 md:px-7 py-10 rounded-[36px] relative border-2 border-[#302C3E] shadow-[0_10px_48px_#2a1e4e36] bg-gradient-to-br from-[#232035] via-[#28253C] to-[#18171E]">
        <div className="relative z-10 flex flex-col items-center">
          {robotEl}
          <div className="absolute top-7 left-[92%] w-48 min-w-[144px] shadow-lg px-4 py-2 rounded-xl font-bold text-[#ea384c] bg-white/95 border border-[#ea384c]/30
          animate-bounce text-sm hidden md:block pointer-events-none"
            style={{
              filter: "drop-shadow(0 0 9px #ea384c44)",
              fontFamily: "inherit",
              fontWeight: 700,
            }}
          >
            Don't even try it…
          </div>
        </div>
        <div className="flex-1 flex flex-col items-start text-white max-w-2xl text-base">
          <h2 className="font-playfair font-bold text-3xl md:text-4xl text-[#a195e0] mb-5 leading-tight flex items-center gap-2 drop-shadow-md">
            <span className="mr-2 tracking-tight">EYE-DAPTICA</span>
            <span className="block text-[#fff]/90 text-2xl">– The All-Seeing Eye of Edaptica</span>
            <span role="img" aria-label="eye" className="ml-2 text-2xl">👁️</span>
          </h2>
          <div className="space-y-4 font-semibold">
            <span className="text-[#66eeff] font-black text-lg bg-[#23263f]/50 px-2 py-1 rounded shadow">Welcome, brave test-taker.</span>
            <p>
              I am <span className="text-[#9b87f5] font-bold">EYE-DAPTICA</span>, forged in the circuits of Edaptica
              to uphold justice, fairness, and <b>full-screen test mode</b>.<br />
              I've been trained on thousands of mock tests and millions of suspicious glances.<br/>
              <b>I don't sleep. I don't blink.</b><br/>
              And I absolutely don't tolerate 
              <span className="bg-[#ea384c]/20 text-[#ea384c] px-2 py-0.5 rounded ml-1 font-bold">
                alt-tabbing
              </span>.
            </p>
            <div className="my-2 bg-[#ea384c]/10 border border-[#ea384c]/30 rounded-xl px-4 py-3 flex items-center gap-2 shadow-sm text-[#ea384c] font-bold w-fit animate-scale-in">
              <span className="text-2xl mr-1">💀📚</span>
              Try to cheat, and I'll activate:
              <span className="ml-2 italic font-extrabold text-[#ef4444]">"Operation Eternal Revision Loop™"</span>
            </div>
            <ul className="list-none pl-0 text-[#d6d2fa] text-base font-medium space-y-1 mt-2">
              <li className="flex items-center gap-2 font-bold">
                <span className="inline-block w-6 h-6 bg-[#1EAEDB] rounded-full text-white flex items-center justify-center font-bold text-sm shadow">🔒</span>
                Stay focused. Stay fair. Stay fabulous.
              </li>
              <li className="ml-8">Because I'm not just watching your screen...</li>
              <li className="ml-8 font-bold text-[#ea384c]">I'm watching your intentions.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EyeDapticaMessage;
